import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as Sentry from "@sentry/angular";
import { Observable, throwError, TimeoutError } from "rxjs";
import { catchError, finalize, tap } from "rxjs/operators";
import { appState } from "src/app/core/state";
import {
  HTTP_CLIENT_DURATION_METRIC,
  shouldSkipHttpClientDurationMetric
} from "src/app/support/sentry-metric-filter";

const URL_PARSE_BASE = "https://invalid.invalid";
const DYNAMIC_PATH_SEGMENT_PATTERN = /^(?=.*\d)[a-zA-Z0-9]+$/;

type HttpMetricOutcome = "success" | "error" | "timeout" | "cancelled";

type HttpMetricAttributes = {
  method: string;
  endpoint: string;
  queryParams: string;
  status_code: number;
  outcome: HttpMetricOutcome;
};

function stripQueryAndHash(url: string): string {
  const withoutQuery: string = url.split("?")[0] ?? url;
  const withoutHash: string = withoutQuery.split("#")[0] ?? withoutQuery;

  return withoutHash;
}

function normalizePathSegment(segment: string): string {
  if (DYNAMIC_PATH_SEGMENT_PATTERN.test(segment)) {
    return ":id";
  }

  return segment;
}

function normalizeEndpoint(pathname: string): string {
  const segments: string[] = pathname.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "/";
  }

  const normalized: string = segments.map(normalizePathSegment).join("/");

  return `/${normalized}`;
}

function normalizeQueryParams(searchParams: URLSearchParams): string {
  const queryParamNames: string[] = Array.from(
    new Set(searchParams.keys())
  ).sort();
  if (queryParamNames.length === 0) {
    return "none";
  }

  return queryParamNames.join(",");
}

function parseQueryParams(url: string): string {
  const queryString: string | undefined = url.split("?")[1]?.split("#")[0];
  if (!queryString) {
    return "none";
  }

  return normalizeQueryParams(new URLSearchParams(queryString));
}

function parseRequestUrl(url: string): {
  pathname: string;
  queryParams: string;
} {
  const urlWithoutQuery: string = stripQueryAndHash(url);

  try {
    const parsed: URL = new URL(url, URL_PARSE_BASE);
    const pathname: string = parsed.pathname || "/";
    const queryParams: string = normalizeQueryParams(parsed.searchParams);

    return {
      pathname,
      queryParams
    };
  } catch {
    const pathname: string = urlWithoutQuery.startsWith("/")
      ? urlWithoutQuery
      : `/${urlWithoutQuery}`;
    const queryParams: string = parseQueryParams(url);

    return {
      pathname,
      queryParams
    };
  }
}

function buildBaseMetricAttributes(
  req: HttpRequest<unknown>
): Omit<HttpMetricAttributes, "status_code" | "outcome"> {
  const { pathname, queryParams } = parseRequestUrl(req.urlWithParams);

  return {
    method: req.method.toUpperCase(),
    endpoint: normalizeEndpoint(pathname),
    queryParams
  };
}

function resolveOutcome(
  statusCode: number,
  hadTerminalEvent: boolean,
  isTimeout: boolean
): HttpMetricOutcome {
  if (isTimeout) {
    return "timeout";
  }

  if (!hadTerminalEvent && statusCode === 0) {
    return "cancelled";
  }

  if (statusCode >= 400 || statusCode === 0) {
    return "error";
  }

  return "success";
}

@Injectable()
export class SentryHttpMetricsInterceptor implements HttpInterceptor {
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (shouldSkipHttpClientDurationMetric(req.urlWithParams)) {
      return next.handle(req);
    }

    const startTime: number = performance.now();
    const baseAttributes: Omit<
      HttpMetricAttributes,
      "status_code" | "outcome"
    > = buildBaseMetricAttributes(req);
    let statusCode: number = 0;
    let hadTerminalEvent: boolean = false;
    let isTimeout: boolean = false;

    return next.handle(req).pipe(
      tap((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          statusCode = event.status;
          hadTerminalEvent = true;
        }
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          statusCode = error.status;
          hadTerminalEvent = true;
        } else if (error instanceof TimeoutError) {
          isTimeout = true;
          hadTerminalEvent = true;
        } else {
          hadTerminalEvent = true;
        }

        return throwError(() => error);
      }),
      finalize(() => {
        const durationMs: number = performance.now() - startTime;
        const outcome: HttpMetricOutcome = resolveOutcome(
          statusCode,
          hadTerminalEvent,
          isTimeout
        );

        Sentry.metrics.distribution(HTTP_CLIENT_DURATION_METRIC, durationMs, {
          unit: "millisecond",
          attributes: {
            ...baseAttributes,
            status_code: statusCode,
            outcome,
            tId: appState.tenantId
          }
        });
      })
    );
  }
}
